import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Layout from "../components/Layout";
import CookieImage from "../components/CookieImage";
import HomePopup from "../components/HomePopup";
import { LOGO_URL, api } from "../lib/api";

// Corner cookies — framed inside hero only. Sizes use clamp() for smooth scaling.
// Translates are softened so each cookie remains visibly rounded (not razor-clipped).
const CORNERS = [
  {
    // Top-left
    key: "brown_butter",
    pos: "top-0 left-0",
    size: "clamp(170px, 34vw, 380px)",
    transform: "translate(-30%, -30%) rotate(-6deg)",
  },
  {
    // Top-right
    key: "cake_batter",
    pos: "top-0 right-0",
    size: "clamp(170px, 34vw, 380px)",
    transform: "translate(30%, -30%) rotate(10deg)",
  },
  {
    // Bottom-left
    key: "snickerdoodle",
    pos: "bottom-0 left-0",
    size: "clamp(160px, 32vw, 360px)",
    transform: "translate(-32%, 28%) rotate(-10deg)",
  },
  {
    // Bottom-right
    key: "brookies",
    pos: "bottom-0 right-0",
    size: "clamp(160px, 32vw, 360px)",
    transform: "translate(32%, 28%) rotate(8deg)",
  },
];

export default function HomePage() {
  const [flavors, setFlavors] = useState([]);
  const [settings, setSettings] = useState({ order_date: "Saturday" });

  useEffect(() => {
    api.get("/flavors").then(r => setFlavors(r.data)).catch(() => {});
    api.get("/settings").then(r => setSettings(r.data)).catch(() => {});
  }, []);

  const pickupLabel = (settings.order_date || "Saturday").toUpperCase();
  const orderBtnText = (settings.order_button_text || "ORDER NOW FOR {date} PICKUP").replace("{date}", pickupLabel);

  return (
    <Layout>
      <HomePopup />
      {/* HERO — min 100svh, content vertically centered, cookies clipped inside */}
      <section
        className="relative flex items-center justify-center overflow-hidden px-6 md:px-12"
        style={{
          minHeight: "100svh",
          paddingTop: "max(5.5rem, env(safe-area-inset-top))",
          paddingBottom: "max(2.5rem, env(safe-area-inset-bottom))",
        }}
        data-testid="hero-section"
      >
        {/* Corner cookies — absolute inside hero, behind content (z-0). Clipped by hero overflow-hidden. */}
        {CORNERS.map((c) => (
          <CookieImage
            key={c.key}
            flavorKey={c.key}
            plateless
            className={`absolute z-0 aspect-square pointer-events-none select-none ${c.pos}`}
            style={{ width: c.size, transform: c.transform }}
          />
        ))}

        {/* Centered content block — above cookies */}
        <div className="relative z-10 w-full max-w-xl flex flex-col items-center text-center">
          <img
            src={LOGO_URL}
            alt="Sugary Sweet"
            className="logo-white w-[62vw] max-w-[340px] sm:max-w-[380px] md:max-w-[420px] h-auto"
            data-testid="hero-logo"
          />

          <h1
            className="font-display font-black text-white uppercase leading-[0.92] tracking-tight mt-8 sm:mt-12"
            style={{ fontSize: "clamp(2rem, 8.5vw, 4.5rem)", letterSpacing: "-0.02em" }}
            data-testid="hero-title"
          >
            <span className="block">Fresh Cookies.</span>
            <span className="block">Made With Love.</span>
          </h1>

          <p
            className="text-white/95 font-bold uppercase mt-6 sm:mt-8 lg:whitespace-nowrap"
            style={{ fontSize: "clamp(0.7rem, 2.4vw, 0.95rem)", letterSpacing: "0.22em" }}
            data-testid="hero-subtitle"
          >
            Gourmet Cookies · Weekly Flavors · Made From Scratch
          </p>

          <div className="w-full mt-10 sm:mt-14 space-y-4 sm:space-y-5 mb-4">
            <Link to="/order" className="hero-pill-solid" data-testid="hero-order-now-btn">
              <span className="flex-1 text-center">{orderBtnText}</span>
              <ArrowRight className="w-5 h-5 ml-2 flex-shrink-0" />
            </Link>
            <Link to="/custom-orders" className="hero-pill-outline" data-testid="hero-custom-order-btn">
              <span className="flex-1 text-center">CUSTOM ORDER</span>
              <ArrowRight className="w-5 h-5 ml-2 flex-shrink-0" />
            </Link>
          </div>
        </div>
      </section>

      {/* THIS WEEK'S FLAVORS */}
      <section className="relative z-10 py-16 sm:py-20 md:py-28" data-testid="weekly-flavors-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-sm uppercase tracking-[0.25em] font-bold text-white/85 drop-shadow">Crafted Weekly</p>
            <h2 className="font-display font-extrabold text-3xl md:text-5xl mt-2 text-white drop-shadow-lg">This Week's Flavors</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {flavors.map((f) => (
              <div key={f.id} className="card-soft p-5 flex flex-col items-center text-center hover:shadow-2xl hover:-translate-y-1 transition-all" data-testid={`home-flavor-card-${f.image_key}`}>
                <CookieImage flavorKey={f.image_key} imageUrl={f.image_url} className="w-32 h-32 md:w-40 md:h-40 rounded-full mb-4" />
                <h3 className="font-display font-bold text-lg md:text-xl leading-tight">{f.name}</h3>
                <p className="text-sm text-[#4B5563] mt-2 leading-relaxed">{f.description}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <Link to="/order" className="btn-white" data-testid="weekly-flavors-order-now-btn">ORDER NOW</Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
